import { API_URL } from "config/api";

const delay = (ms) => new Promise((res) => setTimeout(res, ms));

const WEATHER_CONDITIONS = [
  { condition_code: "moderate_rain", temperature_c: 8, visibility_m: 200 },
  { condition_code: "fog", temperature_c: 6, visibility_m: 80 },
  { condition_code: "light_rain", temperature_c: 10, visibility_m: 500 },
  { condition_code: "cloudy", temperature_c: 12, visibility_m: 1200 },
];

const WEATHER_CYCLE_MS = 10_000;

export const fetchWeather = async () => {
  await delay(300);
  const index = Math.floor(
    (Date.now() / WEATHER_CYCLE_MS) % WEATHER_CONDITIONS.length
  );
  return WEATHER_CONDITIONS[index];
};

const TRAFFIC_CYCLE_MS = 45_000;
const CONGESTION_WINDOW_MS = 15_000;

export const fetchTraffic = async () => {
  await delay(350);
  const elapsed = Date.now() % TRAFFIC_CYCLE_MS;
  const isCongestion = elapsed < CONGESTION_WINDOW_MS;

  if (isCongestion) {
    return {
      status: "congestion",
      description: "Congestion at km 34",
      location: { lat: 42.762, lng: -8.624 },
    };
  }

  return {
    status: "normal",
    description: "Normal flow",
  };
};

const OBU_PING_INTERVAL_MS = 12_000;
const OBU_START = Date.now();

export const fetchOBU = async () => {
  await delay(200);
  const last_ping_seconds = Math.floor(
    ((Date.now() - OBU_START) % OBU_PING_INTERVAL_MS) / 1000
  );

  return {
    active: true,
    vehicle_count: 1,
    last_ping_seconds,
  };
};

const WEATHER_ZONES_CONFIG = [
  [
    {
      id: "WZ-N",
      stationId: "STA-001",
      center: { lat: 42.865, lng: -8.549 },
      radiusM: 4500,
      condition: "moderate_rain",
      precipitation_mm: 4.2,
      visibility_m: 200,
    },
    {
      id: "WZ-C",
      stationId: "STA-002",
      center: { lat: 42.602, lng: -8.641 },
      radiusM: 5500,
      condition: "light_rain",
      precipitation_mm: 1.1,
      visibility_m: 500,
    },
    {
      id: "WZ-S",
      stationId: "STA-003",
      center: { lat: 42.237, lng: -8.724 },
      radiusM: 4000,
      condition: "fog",
      precipitation_mm: 0,
      visibility_m: 80,
    },
  ],
  [
    {
      id: "WZ-N",
      stationId: "STA-001",
      center: { lat: 42.865, lng: -8.549 },
      radiusM: 4500,
      condition: "cloudy",
      precipitation_mm: 0,
      visibility_m: 1200,
    },
    {
      id: "WZ-C",
      stationId: "STA-002",
      center: { lat: 42.602, lng: -8.641 },
      radiusM: 5500,
      condition: "moderate_rain",
      precipitation_mm: 3.8,
      visibility_m: 300,
    },
    {
      id: "WZ-S",
      stationId: "STA-003",
      center: { lat: 42.237, lng: -8.724 },
      radiusM: 4000,
      condition: "light_rain",
      precipitation_mm: 0.8,
      visibility_m: 600,
    },
  ],
  [
    {
      id: "WZ-N",
      stationId: "STA-001",
      center: { lat: 42.865, lng: -8.549 },
      radiusM: 4500,
      condition: "fog",
      precipitation_mm: 0,
      visibility_m: 90,
    },
    {
      id: "WZ-C",
      stationId: "STA-002",
      center: { lat: 42.602, lng: -8.641 },
      radiusM: 5500,
      condition: "cloudy",
      precipitation_mm: 0,
      visibility_m: 1500,
    },
    {
      id: "WZ-S",
      stationId: "STA-003",
      center: { lat: 42.237, lng: -8.724 },
      radiusM: 4000,
      condition: "cloudy",
      precipitation_mm: 0,
      visibility_m: 1400,
    },
  ],
];

const WEATHER_ZONE_CYCLE_MS = 20_000;

export const fetchWeatherZones = async () => {
  await delay(280);
  const idx = Math.floor(
    (Date.now() / WEATHER_ZONE_CYCLE_MS) % WEATHER_ZONES_CONFIG.length
  );
  return WEATHER_ZONES_CONFIG[idx];
};

export const fetchTrafficFlow = async (mode = "live") => {
  const res = await fetch(`${API_URL}/api/v1/traffic?mode=${mode}`);
  if (!res.ok) throw new Error(`Traffic fetch failed: ${res.status}`);
  return res.json();
};

const OBU_PROBE_STEP_MS = 12_000;
const OBU_SEGMENT_SEQ = [
  "N550-SEG-001",
  "N550-SEG-002",
  "N550-SEG-003",
  "N550-SEG-004",
  "N550-SEG-005",
  "N550-SEG-006",
  "N550-SEG-007",
  "N550-SEG-008",
  "N550-SEG-009",
  "N550-SEG-010",
  "N550-SEG-011",
  "N550-SEG-012",
  "N550-SEG-013",
  "N550-SEG-014",
  "N550-SEG-015",
];

const seededLatency = (idx) => 30 + ((idx * 17 + 13) % 81);

export const fetchOBUProbes = async () => {
  await delay(250);
  const obu = await fetchOBU();
  if (!obu.active) return [];

  const now = Date.now();
  const n = OBU_SEGMENT_SEQ.length;
  const curIdx = Math.floor(
    (now % (OBU_PROBE_STEP_MS * n)) / OBU_PROBE_STEP_MS
  );

  return [
    {
      segment_id: OBU_SEGMENT_SEQ[curIdx],
      latency_ms: seededLatency(curIdx),
      recorded_at: now,
    },
    {
      segment_id: OBU_SEGMENT_SEQ[(curIdx - 1 + n) % n],
      latency_ms: seededLatency((curIdx - 1 + n) % n),
      recorded_at: now - OBU_PROBE_STEP_MS,
    },
    {
      segment_id: OBU_SEGMENT_SEQ[(curIdx - 2 + n) % n],
      latency_ms: seededLatency((curIdx - 2 + n) % n),
      recorded_at: now - OBU_PROBE_STEP_MS * 2,
    },
  ];
};
